/**
 * Human-readable description of a board-dictated opening (`MapDef.fixedSetup`).
 *
 * Maps that declare one decide their own player count, ownership and armies, so
 * the matching game-setup controls are replaced by read-only text. That text
 * has to SAY WHAT THE BOARD DOES rather than just "not configurable" — the
 * player still needs to know the supported seats, starting area, and garrison
 * they are walking into.
 *
 * Derived entirely from map data, so it stays correct when the map is
 * regenerated (Capture the Flag renumbers every territory on a trace re-import).
 */
import { cardBonusScheduleInfo, type MapDef } from '@warcarp/api-types'

export interface FixedSetupDescription {
  /** Replaces the Players stepper. */
  players: string
  /** Replaces the Territory Selection segmented control. */
  territorySelection: string
  /** Territory selection in a few words, for the locked control and summaries. */
  homeArea: string
  /** Replaces the Starting Troops stepper. */
  startingTroops: string
  /**
   * Config the board PINS, as label → readable value. Generic: whatever the map
   * lists in `fixedSetup.config` shows up here, so a future map pinning a
   * different rule needs no UI change.
   */
  pinnedConfig: Array<{ key: string; label: string; value: string }>
}

/** How each GameConfig field reads when a board pins it. */
const CONFIG_LABELS: Record<string, { label: string; format: (v: unknown) => string }> = {
  cardBonusSchedule: {
    label: 'Card Set Bonus',   // must match the control it replaces
    format: (v) => { const info = cardBonusScheduleInfo(v); return `${info.label} — ${info.sequence}` },
  },
  maxAttacksPerTurn: { label: 'Attacks Per Turn', format: (v) => (v === null ? 'Unlimited' : String(v)) },
  maxFortifiesPerTurn: { label: 'Fortifies Per Turn', format: (v) => (v === null ? 'Unlimited' : String(v)) },
  eliminatePlayerBonus: { label: 'Eliminate Bonus', format: (v) => `${v} armies` },
  maxTurns: { label: 'Turn Limit', format: (v) => String(v) },
  startingTroops: { label: 'Starting Troops per Territory', format: (v) => String(v) },
  autoAssignTerritories: { label: 'Territory Selection', format: (v) => (v ? 'Random' : 'Manual claim') },
}

export function describeFixedSetup(map: MapDef): FixedSetupDescription | null {
  const fixed = map.fixedSetup
  if (!fixed) return null

  const nameOf = new Map(map.territories.map((t) => [t.id, t.name]))
  // Every area is the same shape on a symmetric board, so describe the first
  // and let the count carry the rest.
  const sample = fixed.areas[0]
  const seeded = Object.entries(sample?.armies ?? {})
  // "Fort" / "Tower" — the last word of the territory name is what varies
  // between areas ("Iron Fort" vs "Mire Fort"), so it names the ROLE.
  const roleOf = (id: string) => (nameOf.get(id) ?? id).split(' ').slice(-1)[0] ?? id
  // Just what YOU start on. The neutral garrisons are the board's business and
  // reading them out here buried the one line that matters.
  const startingTroops =
    seeded.map(([id, armies]) => `${armies} on ${roleOf(id).toLowerCase()}`).join(', ') ||
    'Set by the map'

  // Anything the board pins, in a stable order, with a readable value. An
  // unrecognised key still renders — better a raw key than a silent omission.
  const pinnedConfig = Object.entries(fixed.config ?? {})
    .map(([key, v]) => ({
      key,
      label: CONFIG_LABELS[key]?.label ?? key,
      value: CONFIG_LABELS[key]?.format(v) ?? String(v),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const minPlayers = fixed.minPlayers ?? fixed.playerCount
  const playerRange = minPlayers === fixed.playerCount
    ? `Exactly ${fixed.playerCount}`
    : `${minPlayers}–${fixed.playerCount}`
  const unusedAreas = minPlayers < fixed.playerCount
    ? ` Unused starting areas remain neutral with the board's garrison.`
    : ''

  return {
    pinnedConfig,
    players: playerRange,
    homeArea: 'Random home area',
    territorySelection:
      `Each player is dealt one of the ${fixed.areas.length} starting areas at random. ` +
      `Everything else on the board starts neutral.${unusedAreas}`,
    startingTroops,
  }
}
